import { useHotkeys } from 'react-hotkeys-hook';
import type { InboxItem } from '@/types/inbox';
import { InboxRow } from './InboxRow';

interface InboxListProps {
  items: InboxItem[];
  selectedId: string | null;
  expandedIds: Set<string>;
  checkedIds: Set<string>;
  onSelect: (id: string) => void;
  onToggleExpand: (id: string) => void;
  onExpand: (id: string) => void;
  onCollapse: (id: string) => void;
  onToggleChecked: (id: string) => void;
}

const HOTKEY_GROUP = { group: 'Inbox' };

export function InboxList({
  items,
  selectedId,
  expandedIds,
  checkedIds,
  onSelect,
  onToggleExpand,
  onExpand,
  onCollapse,
  onToggleChecked,
}: InboxListProps) {
  // Routed via react-hotkeys-hook so each shortcut surfaces in the
  // Shift+? cheatsheet. The hook skips form fields by default, so typing
  // in the search input never triggers row navigation.
  const selectedIndex = items.findIndex((it) => it.inboxItemId === selectedId);

  useHotkeys(
    'down',
    (event) => {
      if (items.length === 0) return;
      event.preventDefault();
      const next =
        items[Math.min(selectedIndex + 1, items.length - 1)] ?? items[0];
      onSelect(next.inboxItemId);
    },
    { description: 'Select next item', metadata: HOTKEY_GROUP },
    [items, selectedIndex, onSelect],
  );

  useHotkeys(
    'up',
    (event) => {
      if (items.length === 0) return;
      event.preventDefault();
      const next = items[Math.max(selectedIndex - 1, 0)] ?? items[0];
      onSelect(next.inboxItemId);
    },
    { description: 'Select previous item', metadata: HOTKEY_GROUP },
    [items, selectedIndex, onSelect],
  );

  useHotkeys(
    ['right', 'enter'],
    (event) => {
      if (!selectedId) return;
      event.preventDefault();
      // Expand without touching other open rows so the user can compare.
      if (!expandedIds.has(selectedId)) onExpand(selectedId);
    },
    { description: 'Expand item', metadata: HOTKEY_GROUP },
    [selectedId, expandedIds, onExpand],
  );

  useHotkeys(
    ['left', 'escape'],
    (event) => {
      if (!selectedId) return;
      event.preventDefault();
      if (expandedIds.has(selectedId)) onCollapse(selectedId);
    },
    { description: 'Collapse item', metadata: HOTKEY_GROUP },
    [selectedId, expandedIds, onCollapse],
  );

  useHotkeys(
    'x',
    (event) => {
      // Gmail/Linear convention: `x` toggles selection on the focused row.
      if (!selectedId) return;
      event.preventDefault();
      onToggleChecked(selectedId);
    },
    { description: 'Toggle selection', metadata: HOTKEY_GROUP },
    [selectedId, onToggleChecked],
  );

  return (
    <ul className="overflow-hidden rounded-lg border border-border bg-card">
      {items.map((item) => (
        <InboxRow
          key={item.inboxItemId}
          item={item}
          isSelected={item.inboxItemId === selectedId}
          isExpanded={expandedIds.has(item.inboxItemId)}
          isChecked={checkedIds.has(item.inboxItemId)}
          onSelect={() => onSelect(item.inboxItemId)}
          onToggleExpand={() => onToggleExpand(item.inboxItemId)}
          onToggleChecked={() => onToggleChecked(item.inboxItemId)}
        />
      ))}
    </ul>
  );
}
